import React, { useState, useMemo } from 'react';
import { CheckboxV2, Flex, ButtonV2, TextV2, InputV2, Box, IconV2, TooltipV2 } from '@withjoy/joykit';
import { Search, Close, Add, Trash } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { useFilteredData } from '@shared/utils/hooks/useFilteredData';
import { getInitialsFromName } from '@shared/utils/getInitials';
import { RecipientSelectable } from '@apps/guestList/GuestList.types';
import { StyledBox } from './RecipientsPicker.styles';
import Filter from './components/Filter/Filter';

interface RecipientsPickerProps {
  recipients: RecipientSelectable[];
  setRecipients: React.Dispatch<React.SetStateAction<RecipientSelectable[]>>;
  createRecipient: () => void;
  updateRecipient: (contactId: string) => void;
  deleteRecipients: () => void;
}

export type Filter = {
  id: string;
  label: string;
  value: boolean;
};

const peopleFilterKeys = ['fullName'] as const;

// filter definitions
const shouldFilterByMissingAddresses = (filters: Filter[]) => !!filters.find(({ id }) => id === 'missingAddresses')?.value;
const filterByMissingAddresses = (recipientsFiltered: readonly RecipientSelectable[]) =>
  recipientsFiltered.filter(recipient => !recipient?.address?.address1 && !recipient?.legacyAddressString);

const applyFilters = (recipientsFiltered: readonly RecipientSelectable[], filters: Filter[]) => {
  let recipientsView = recipientsFiltered;

  const filteredByMissingAddress = shouldFilterByMissingAddresses(filters);
  if (filteredByMissingAddress) {
    recipientsView = filterByMissingAddresses(recipientsFiltered);
  }

  return recipientsView;
};

const RecipientsPicker: React.FC<RecipientsPickerProps> = ({ recipients, setRecipients, createRecipient, updateRecipient, deleteRecipients }) => {
  const { t } = useTranslation('guestList');
  const tAdmin = t('admin');
  const [searchString, setSearchString] = useState<string>('');
  const [filters, setFilters] = useState<Filter[]>([{ id: 'missingAddresses', label: tAdmin.missingAddresses(), value: false }]);
  const [recipientsFiltered = []] = useFilteredData(recipients, searchString, peopleFilterKeys);

  const isAnyFilterActive = useMemo(() => !!filters.find(({ value }) => value), [filters]);

  const recipientsView = applyFilters(recipientsFiltered, filters);

  const selectAllIsOn = useMemo(() => !!recipients.length && recipients.every(recipient => recipient?.selected), [recipients]);

  const selectedRecipientsQuantity = useMemo(() => recipients.filter(({ selected }) => selected).length, [recipients]);

  const handleOnSelectAllClick = () => {
    if (recipients.every(recipient => recipient?.selected)) {
      setRecipients(prevState => prevState.map(recipient => ({ ...recipient, selected: false })));
    } else {
      setRecipients(prevState => prevState.map(recipient => ({ ...recipient, selected: true })));
    }
  };

  const handleOnSelectRecipientChange = (recipientsViewItem: RecipientSelectable) => {
    setRecipients(prevState => prevState.map(recipient => ({ ...recipient, selected: recipientsViewItem.id === recipient.id ? !recipient.selected : recipient.selected })));
  };

  return (
    <Flex justifyContent="flex-start" flexDirection="column" padding={6} rowGap={6}>
      {!!selectedRecipientsQuantity && (
        <ButtonV2
          width="180px"
          position="fixed"
          right={6}
          bottom={6}
          zIndex={100}
          shape="rounded"
          variant="solid"
          intent="neutral"
          startIcon={<Trash />}
          onClick={deleteRecipients}
        >
          {`${selectedRecipientsQuantity > 1 ? tAdmin.deleteGuests() : tAdmin.deleteGuest()}`}
        </ButtonV2>
      )}
      {!selectedRecipientsQuantity && (
        <ButtonV2 width="180px" position="fixed" right={6} bottom={6} zIndex={100} shape="rounded" variant="solid" intent="neutral" startIcon={<Add />} onClick={createRecipient}>
          {tAdmin.addGuest()}
        </ButtonV2>
      )}
      <Flex alignItems="center" justifyContent="center" columnGap={2}>
        <InputV2
          value={searchString}
          onChange={event => setSearchString(event.target.value)}
          startElement={<Search />}
          endElement={
            searchString && (
              <IconV2 onClick={() => setSearchString('')}>
                <Close />
              </IconV2>
            )
          }
          placeholder="Search"
          overrides={{
            Root: {
              props: {
                borderRadius: '1000px',
                _hover: {
                  borderRadius: '1000px'
                },
                _focus: {
                  borderRadius: '1000px'
                },
                _after: {
                  borderRadius: '1000px'
                }
              }
            },
            EndElement: {
              props: { pointerEvents: 'initial', cursor: 'pointer' }
            }
          }}
        />
        <Filter filters={filters} setFilters={setFilters} />
      </Flex>
      <Flex alignItems="center" justifyContent="space-between">
        <TextV2 typographyVariant="body2" color="mono14" fontWeight={400}>
          {tAdmin.selected()} ({recipients.filter(recipient => recipient?.selected).length})
        </TextV2>
        {!searchString && !!recipients.length && (
          <ButtonV2
            variant="link"
            onClick={handleOnSelectAllClick}
            typographyVariant="body2"
            fontWeight={400}
            _hover={{ color: 'mono14' }}
            _active={{ color: 'mono14' }}
            color="mono14"
          >
            {selectAllIsOn ? tAdmin.unSelectAll() : tAdmin.selectAll()}
          </ButtonV2>
        )}
      </Flex>
      <Flex flexDirection="column">
        {!recipientsView.length ? (
          <Flex alignItems="center" justifyContent="center" height="100%" width="100%">
            <TextV2>{tAdmin.noGuests()}.</TextV2>
          </Flex>
        ) : (
          recipientsView.map(
            recipientsViewItem =>
              recipientsViewItem && (
                <Flex
                  key={recipientsViewItem.id}
                  alignItems="center"
                  justifyContent="space-around"
                  paddingX="5px"
                  _hover={{ backgroundColor: 'mono2' }}
                  _active={{ backgroundColor: 'mono3' }}
                  borderRadius="4px"
                  backgroundColor={recipientsViewItem.selected ? 'mono2' : 'transparent'}
                >
                  <CheckboxV2
                    paddingY="8px"
                    isChecked={recipientsViewItem.selected}
                    onChange={() => handleOnSelectRecipientChange(recipientsViewItem)}
                    typographyVariant="body2"
                    fontWeight={400}
                  />
                  <Flex
                    paddingBottom={recipientsViewItem.partyIndicator === 'last' ? '4px' : '0px'}
                    paddingX="16px"
                    width="100%"
                    onClick={() => {
                      updateRecipient(recipientsViewItem.id);
                    }}
                    cursor="pointer"
                    alignItems="center"
                  >
                    <StyledBox $partyIndicator={isAnyFilterActive || searchString ? 'none' : recipientsViewItem.partyIndicator}>
                      <Flex
                        fontSize="14px"
                        paddingY="2px"
                        paddingX="6px"
                        backgroundColor="mono14"
                        borderRadius="50%"
                        color="white"
                        height="30px"
                        width="30px"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {getInitialsFromName(`${recipientsViewItem.firstName || 'f'} ${recipientsViewItem.lastName || 'l'}`, { transform: 'uppercase' })}
                      </Flex>
                    </StyledBox>
                    <TooltipV2 content={recipientsViewItem.fullName} placement="topStart" onMouseEnterDelay={500}>
                      <Box marginLeft="10px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" maxWidth="150px">
                        {recipientsViewItem.fullName}
                      </Box>
                    </TooltipV2>
                  </Flex>
                </Flex>
              )
          )
        )}
      </Flex>
    </Flex>
  );
};

export default RecipientsPicker;
